import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import WebMercatorViewport from 'viewport-mercator-project'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
// @ts-ignore
import { createPortal } from 'react-dom'

	const RECENTS_KEY = 'shekl.web.mapbox.searchbox.recents.v1'
	const MAX_RECENTS = 8

	function loadRecents() {
		try {
			const raw = localStorage.getItem(RECENTS_KEY)
			if (!raw) return []
			const parsed = JSON.parse(raw)
			if (!Array.isArray(parsed)) return []
			return parsed
				.filter((p) => p && typeof p.placeName === 'string' && Array.isArray(p.coordinate) && p.coordinate.length === 2)
				.slice(0, MAX_RECENTS)
		} catch {
			return []
		}
	}

	function saveRecent(p) {
		try {
			const existing = loadRecents()
			const key = (p?.id ?? p?.placeName ?? '').toString()
			const deduped = [p, ...existing.filter((x) => (x?.id ?? x?.placeName ?? '').toString() !== key)]
			localStorage.setItem(RECENTS_KEY, JSON.stringify(deduped.slice(0, MAX_RECENTS)))
			return deduped.slice(0, MAX_RECENTS)
		} catch {
			return loadRecents()
		}
	}

	function matchRecents(recents, q, limit) {
		const query = (q || '').trim().toLowerCase()
		if (!query) return recents.slice(0, limit)
		return recents
			.filter((r) => (r.placeName || '').toLowerCase().includes(query))
			.slice(0, limit)
	}

/** @type {import('react').ForwardRefExoticComponent<any>} */
const GeocoderControl = forwardRef(function GeocoderControl(props, ref) {

		const {
			mapRef,
			mapboxApiAccessToken,
			onInit,
			onLoading,
			onResults,
			onResult,
			onError,
			onClear,
			containerNode,
			placeholder,
			minLength,
			limit,
			trackProximity,
			types,
			countries,
			language,
		} = props

		const debounceRef = useRef(null)
		const sessionTokenRef = useRef(String(Date.now()))
		const lastSuggestSeqRef = useRef(0)

		const FALLBACK_LAT = 33.13845
		const FALLBACK_LNG = -95.60107

		const [input, setInput] = useState('')
		const [value, setValue] = useState(null)
		const [loading, setLoading] = useState(false)
		const [remoteResults, setRemoteResults] = useState([])
		const [recents, setRecents] = useState(() => loadRecents())
		const [geoProximity, setGeoProximity] = useState(() => `${FALLBACK_LNG},${FALLBACK_LAT}`)

		useEffect(() => {
			onInit?.({})
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [])

		useEffect(() => {
			if (!trackProximity) return
			if (!('geolocation' in navigator)) return

			let cancelled = false
			navigator.geolocation.getCurrentPosition(
				(pos) => {
					if (cancelled) return
					const lat = pos?.coords?.latitude
					const lng = pos?.coords?.longitude
					if (typeof lat !== 'number' || typeof lng !== 'number') return
					setGeoProximity(`${lng},${lat}`)
				},
				() => {
					// Permission denied / unavailable — keep fallback.
				},
				{ enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 }
			)

			return () => {
				cancelled = true
			}
		}, [trackProximity])

		useImperativeHandle(ref, () => ({
			handleClear: () => {
				setInput('')
				setValue(null)
				setRemoteResults([])
				onClear?.()
			}
		}), [onClear])

		const proximity = useMemo(() => {
			if (!trackProximity) return ''
			return geoProximity
		}, [geoProximity, trackProximity])

		const displayResults = useMemo(() => {
			const q = input.trim()
			const recentMatches = matchRecents(recents, q, 5)
			if (q.length < (minLength ?? 2)) return recentMatches

			const seen = new Set()
			const merged = []
			for (const r of recentMatches) {
				const key = (r.placeName || '').trim().toLowerCase()
				if (!key || seen.has(key)) continue
				seen.add(key)
				merged.push({ id: r.id, placeName: r.placeName, coordinate: r.coordinate })
			}
			for (const r of remoteResults) {
				const key = (r.placeName || '').trim().toLowerCase()
				if (!key || seen.has(key)) continue
				seen.add(key)
				merged.push({ ...r })
				if (merged.length >= 5) break
			}
			return merged
		}, [input, minLength, recents, remoteResults])

		useEffect(() => {
			const q = input.trim()
			if (q.length < (minLength ?? 2)) {
				setRemoteResults([])
				return
			}

			if (debounceRef.current) window.clearTimeout(debounceRef.current)
			debounceRef.current = window.setTimeout(async () => {
				const seq = ++lastSuggestSeqRef.current
				setLoading(true)
				onLoading?.({})
				try {
					const proximityParam = proximity ? `&proximity=${encodeURIComponent(proximity)}` : ''
					const languageParam = `&language=${encodeURIComponent(language || 'en')}`
					const typesValue = (types || 'address,poi').split(',').map((x) => x.trim()).filter(Boolean).join(',')
					const typesParam = `&types=${encodeURIComponent(typesValue)}`
					const countriesValue = (countries || '').split(',').map((x) => x.trim()).filter(Boolean).join(',')
					const countriesParam = countriesValue ? `&country=${encodeURIComponent(countriesValue)}` : ''
					const url = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(q)}${languageParam}&limit=${encodeURIComponent(limit ?? 5)}${proximityParam}&session_token=${encodeURIComponent(sessionTokenRef.current)}${typesParam}${countriesParam}&access_token=${encodeURIComponent(mapboxApiAccessToken)}`
					const res = await fetch(url)
					const json = await res.json()
					if (seq !== lastSuggestSeqRef.current) return
					const suggestions = (json?.suggestions ?? [])
					const mapped = suggestions.map((s) => ({
						id: String(s.mapbox_id ?? Math.random()),
						placeName: `${s.name ? s.name + ', ' : ''}${s.place_formatted || s.full_address || ''}`,
						coordinate: [0, 0],
					}))
					setRemoteResults(mapped)
					onResults?.({ results: mapped })
				} catch (e) {
					if (seq !== lastSuggestSeqRef.current) return
					setRemoteResults([])
					onError?.(e)
				} finally {
					if (seq === lastSuggestSeqRef.current) setLoading(false)
				}
			}, 300)

			return () => {
				if (debounceRef.current) window.clearTimeout(debounceRef.current)
			}
		}, [countries, input, language, limit, mapboxApiAccessToken, minLength, onError, onLoading, onResults, proximity, types])

		async function handleSelect(r) {
			try {
				const retrieveUrl = `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(r.id)}?session_token=${encodeURIComponent(sessionTokenRef.current)}&access_token=${encodeURIComponent(mapboxApiAccessToken)}`
				const res = await fetch(retrieveUrl)
				const json = await res.json()
				const feature = json?.features?.[0]
				const coords = feature?.geometry?.coordinates
				if (!coords || coords.length !== 2) throw new Error('Unable to get location coordinates.')
				const [longitude, latitude] = coords

				const placeName = r.placeName || feature?.properties?.name || feature?.properties?.full_address || feature?.properties?.place_formatted || ''
				const result = {
					place_name: placeName,
					center: [longitude, latitude],
					bbox: feature?.bbox,
					properties: {
						key: Math.random(),
						short_code: 'us'
					}
				}

				let zoom = 16
				try {
					const m = mapRef?.getMap?.()
					const rect = m?.getContainer?.()?.getBoundingClientRect?.()
					const width = rect?.width ?? 600
					const height = rect?.height ?? 400
					const bbox = feature?.bbox
					if (bbox && bbox.length === 4) {
						const fit = new WebMercatorViewport({ width, height }).fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]])
						zoom = fit.zoom
					}
				} catch {
					// ignore
				}

				onResult?.({ result }, { longitude, latitude, zoom })
				setRecents(saveRecent({ id: String(r.id), placeName, coordinate: [longitude, latitude] }))

				setInput('')
				setRemoteResults([])
				sessionTokenRef.current = String(Date.now())
			} catch (e) {
				onError?.(e)
			}
		}

		const ui = (
			<div>
				<Autocomplete
					value={value}
					onChange={(_, newValue, reason) => {
						if (reason === 'selectOption' && newValue) {
							handleSelect(newValue)
						}
						setValue(newValue)
					}}
					inputValue={input}
					onInputChange={(_, newInputValue, reason) => {
						if (reason === 'clear') {
							setInput('')
							setValue(null)
							setRemoteResults([])
							onClear?.()
							return
						}
						setInput(newInputValue)
					}}
					options={displayResults}
					loading={loading}
					openOnFocus
					autoHighlight
					filterOptions={(x) => x}
					isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
					getOptionLabel={(opt) => opt?.placeName || ''}
					renderOption={(optionProps, option) => (
						<li {...optionProps} key={option.id}>
							<div style={{ width: '100%' }}>
								<div>{option.placeName}</div>
								{option._kind === 'recent' && (
									<div className="small text-muted">Recent</div>
								)}
							</div>
						</li>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder={placeholder || 'Add Stop'}
							onKeyDown={(e) => {
								if (e.key === 'Escape') {
									setInput('')
									setValue(null)
									setRemoteResults([])
									onClear?.()
								}
							}}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loading ? <CircularProgress color="inherit" size={16} /> : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
							className="form-control border-app"
						/>
					)}
				/>
			</div>
		)

		if (containerNode) {
			return createPortal(ui, containerNode)
		}
		return ui
	})

	GeocoderControl.defaultProps = {
		onInit: () => { },
		onLoading: () => { },
		onResults: () => { },
		onResult: () => { },
		onError: () => { },
		onClear: () => { },
		placeholder: 'Search',
		trackProximity: false,
		minLength: 2,
		limit: 5,
	}

	GeocoderControl.propTypes = {
		mapRef: PropTypes.any.isRequired,
		mapboxApiAccessToken: PropTypes.string.isRequired,
		onInit: PropTypes.func,
		onLoading: PropTypes.func,
		onResults: PropTypes.func,
		onResult: PropTypes.func,
		onError: PropTypes.func,
		onClear: PropTypes.func,
		containerNode: PropTypes.any,
		placeholder: PropTypes.string,
		trackProximity: PropTypes.bool,
		minLength: PropTypes.number,
		limit: PropTypes.number,
	}

	export default GeocoderControl;
